/**
 * Custom hook for managing Irrigation System Monitoring persistence
 * Stores and retrieves user preferences from localStorage
 */

const React = require('react');
const { useState, useEffect } = React;
const { getItem, setItem } = require('localStorageManager');

const STORAGE_KEYS = {
  LAST_VIEW_MODE: 'irrigation_monitoring_view_mode',
  IS_ADVANCED_MODE: 'irrigation_monitoring_advanced_mode',
  LAST_SELECTED_FIELD_ID: 'irrigation_monitoring_selected_field',
  LAST_SELECTED_FARMING_UNIT_ID: 'irrigation_monitoring_selected_farming_unit',
  HAS_SEEN_BANNER: 'irrigation_monitoring_has_seen_banner',
};

/**
 * Hook for managing persistent state
 * @returns {Object} Persistence state and setters
 */
const useIrrigationPersistence = () => {
  const [viewMode, setViewModeState] = useState('Farm');
  const [isAdvancedMode, setIsAdvancedModeState] = useState(false);
  const [selectedFieldId, setSelectedFieldIdState] = useState(null);
  const [selectedFarmingUnitId, setSelectedFarmingUnitIdState] = useState(null);
  const [hasSeenBanner, setHasSeenBannerState] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const loadFromStorage = () => {
      try {
        const savedViewMode = getItem(STORAGE_KEYS.LAST_VIEW_MODE, 'Farm');
        const savedAdvancedMode =
          getItem(STORAGE_KEYS.IS_ADVANCED_MODE, 'false') === 'true';
        const savedFieldId = getItem(STORAGE_KEYS.LAST_SELECTED_FIELD_ID, null);
        const savedFarmingUnitId = getItem(
          STORAGE_KEYS.LAST_SELECTED_FARMING_UNIT_ID,
          null,
        );
        const savedBannerState =
          getItem(STORAGE_KEYS.HAS_SEEN_BANNER, 'false') === 'true';

        setViewModeState(savedViewMode);
        setIsAdvancedModeState(savedAdvancedMode);
        setSelectedFieldIdState(savedFieldId);
        setSelectedFarmingUnitIdState(savedFarmingUnitId);
        setHasSeenBannerState(savedBannerState);
        setIsInitialized(true);
      } catch (error) {
        console.error(
          'Error loading irrigation monitoring preferences:',
          error,
        );
        setIsInitialized(true);
      }
    };

    loadFromStorage();
  }, []);

  /**
   * Set view mode and persist to localStorage
   * @param {'Farm' | 'Field'} mode
   */
  const setViewMode = (mode) => {
    try {
      setViewModeState(mode);
      setItem(STORAGE_KEYS.LAST_VIEW_MODE, mode);
    } catch (error) {
      console.error('Error saving view mode:', error);
    }
  };

  /**
   * Toggle advanced mode and persist to localStorage
   */
  const toggleAdvancedMode = () => {
    try {
      const newValue = !isAdvancedMode;
      setIsAdvancedModeState(newValue);
      setItem(STORAGE_KEYS.IS_ADVANCED_MODE, String(newValue));
    } catch (error) {
      console.error('Error saving advanced mode:', error);
    }
  };

  /**
   * Set selected field ID and persist to localStorage
   * @param {string|null} fieldId
   */
  const setSelectedFieldId = (fieldId) => {
    try {
      setSelectedFieldIdState(fieldId);
      if (fieldId) {
        setItem(STORAGE_KEYS.LAST_SELECTED_FIELD_ID, fieldId);
      }
    } catch (error) {
      console.error('Error saving selected field ID:', error);
    }
  };

  /**
   * Set selected farming unit ID and persist to localStorage
   * @param {string|null} farmingUnitId
   */
  const setSelectedFarmingUnitId = (farmingUnitId) => {
    try {
      setSelectedFarmingUnitIdState(farmingUnitId);
      if (farmingUnitId) {
        setItem(STORAGE_KEYS.LAST_SELECTED_FARMING_UNIT_ID, farmingUnitId);
      }
    } catch (error) {
      console.error('Error saving selected farming unit ID:', error);
    }
  };

  /**
   * Mark banner as seen and persist to localStorage
   */
  const markBannerAsSeen = () => {
    try {
      setHasSeenBannerState(true);
      setItem(STORAGE_KEYS.HAS_SEEN_BANNER, 'true');
    } catch (error) {
      console.error('Error saving banner state:', error);
    }
  };

  /**
   * Clear all stored preferences (for testing or reset)
   */
  const clearAllPreferences = () => {
    try {
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
      setViewModeState('Farm');
      setIsAdvancedModeState(false);
      setSelectedFieldIdState(null);
      setSelectedFarmingUnitIdState(null);
      setHasSeenBannerState(false);
    } catch (error) {
      console.error('Error clearing preferences:', error);
    }
  };

  return {
    // State
    viewMode,
    isAdvancedMode,
    selectedFieldId,
    selectedFarmingUnitId,
    hasSeenBanner,
    isInitialized,

    // Setters
    setViewMode,
    toggleAdvancedMode,
    setSelectedFieldId,
    setSelectedFarmingUnitId,
    markBannerAsSeen,
    clearAllPreferences,
  };
};

module.exports = useIrrigationPersistence;
